/**
 * Represents a single active grid overlay on an element.
 * Multiple instances can coexist on the same element.
 */
export interface GridInstance {
  /** Unique identifier for this grid instance */
  readonly id: string;

  /** Adapter type key (e.g. "columns", "phi", "rule-of-thirds") */
  readonly type: string;

  /** Whether this grid is currently rendered */
  visible: boolean;

  /** Custom display name; falls back to adapter.label when absent */
  name?: string;

  /** Adapter-specific settings, keyed by UIFieldDescriptor.key */
  settings: Record<string, any>;
}

/**
 * Breakpoint label — S is always smallest, XL is always largest.
 * The number of active breakpoints determines which labels are used:
 *   2 layouts → S, M
 *   3 layouts → S, M, L
 *   4 layouts → S, M, L, XL
 */
export type GridLayoutLabel = "S" | "M" | "L" | "XL";

export const GRID_LAYOUT_LABELS: GridLayoutLabel[] = ["S", "M", "L", "XL"];

export const MIN_LAYOUTS = 2;
export const MAX_LAYOUTS = 4;

/** Default minWidth values for each breakpoint slot (index 0 = largest) */
const DEFAULT_MIN_WIDTHS = [1280, 1024, 768, 0];

/**
 * Returns the label for a layout given its index in a sorted (desc) array.
 * index 0 = largest → last label; index n-1 = smallest → "S"
 */
export function getLayoutLabel(index: number, total: number): GridLayoutLabel {
  const labelIndex = total - 1 - index;
  return GRID_LAYOUT_LABELS[labelIndex] ?? "S";
}

/**
 * A named responsive layout containing a set of grid instances.
 * Each element can have multiple layouts which resolve automatically by width.
 */
export interface GridLayout {
  /** Unique identifier for this layout */
  readonly id: string;

  /** Breakpoint label (S / M / L / XL) */
  label: GridLayoutLabel;

  /** Minimum viewport width required for this layout to become active */
  minWidth: number;

  /** Grid instances active in this layout */
  instances: GridInstance[];
}

/**
 * Serializable snapshot of all layouts on one element.
 * Used for localStorage persistence.
 */
export interface GridElementState {
  readonly elementId: string;
  layouts: GridLayout[];
  selectedLayoutId: string;
  panelTitle?: string;
}

let nextId = 0;

export function createGridInstanceId(): string {
  return `grid-${Date.now()}-${nextId++}`;
}

export function createGridLayoutId(): string {
  return `layout-${Date.now()}-${nextId++}`;
}

/**
 * Creates a fresh set of layouts with the given count (2–4).
 * Layouts are sorted descending by minWidth (largest first).
 */
export function createDefaultLayouts(count: number = MIN_LAYOUTS): GridLayout[] {
  const safeCount = Math.max(MIN_LAYOUTS, Math.min(MAX_LAYOUTS, count));
  const layouts: GridLayout[] = [];

  for (let i = 0; i < safeCount; i++) {
    const label = getLayoutLabel(i, safeCount);
    const minWidth = i === safeCount - 1 ? 0 : (DEFAULT_MIN_WIDTHS[i] ?? 0);
    layouts.push({
      id: createGridLayoutId(),
      label,
      minWidth,
      instances: [],
    });
  }

  return layouts;
}

export function normalizeGridLayoutMinWidth(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.round(value));
}

/**
 * Re-assigns labels after the layout count changes.
 * Layouts must already be sorted descending by minWidth.
 */
export function reassignLabels(layouts: GridLayout[]): void {
  const total = layouts.length;
  for (let i = 0; i < total; i++) {
    layouts[i].label = getLayoutLabel(i, total);
  }
}
